import React, { useState, useRef, useContext, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { Color, Vector3 } from "three";
import * as THREE from 'three';
import styled from 'styled-components';


const mainColor = new Color().setColorName("crimson");
const selectionColor = new Color("gold");

const StyledLink = styled.a`
    text-decoration: none !important;
    color: black;

    &:hover {
		text-decoration: none !important;
        color: ${selectionColor.getStyle()};
    }

    &:visited {
		text-decoration: none !important;
        color: ${selectionColor.multiplyScalar(0.8).getStyle()};
		&:hover {
			color: ${selectionColor.getStyle()};
		}
    }
`;

const StyledInfoBubble = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    background-color: ${mainColor.getStyle()};
    padding: .5rem;
    border-radius: 1rem;
    box-shadow: 0rem 0rem 1rem rgba(0, 0, 0, 0.5);
    font-family: 'Arial', sans-serif;
    font-size: 15px;
    white-space: pre-wrap;
    word-wrap: break-word;
    min-width: 275px;
`;

const StyledListItem = styled.li`
    text-decoration: none;
    padding: 5px;
    font-family: 'Teko', Sans-serif;
    font-size: 16px;
    font-weight: 600;
    transition: all .4s ease;
`;

// Erstellen Sie einen Kontext für den ausgewählten InfoPoint
const SelectedInfoPointContext = React.createContext();

export function InfoPointProvider({ children }) {
	const [selectedInfoPoint, setSelectedInfoPoint] = useState(null);
	return (
		<SelectedInfoPointContext.Provider value={{ selectedInfoPoint, setSelectedInfoPoint }}>
			{children}
		</SelectedInfoPointContext.Provider>
	);
}

let nextId = 0;
export function InfoPoint({ position, lookingAt, title, info, link, icon_path, isRotating, carPosition }) {
	const id = useRef(nextId++).current;
	const ref = useRef();
	const { camera } = useThree();
	const { selectedInfoPoint, setSelectedInfoPoint } = useContext(SelectedInfoPointContext);

	const [isHovered, setIsHovered] = useState(false); // New state variable for hover

	const targetPosition = useRef(new Vector3());
	const isAnimating = useRef(false);

	const infoRef = useRef(); // Ref for the info text

	// Animate the camera to the position of the info point when clicked
	const handleClick = () => {
		// Calculate the target position for the camera
		const carToInfoPoint = new Vector3(...position).sub(new Vector3(...carPosition));
		const distance = carToInfoPoint.length();
		const direction = carToInfoPoint.normalize();
		targetPosition.current.copy(direction.multiplyScalar(distance * 1.2).add(new Vector3(...position)));

		setSelectedInfoPoint(id); // Set this info point as the selected one
	};

	// Close the info text when clicking outside of it
	const handleClickOutside = (event) => {
		if (infoRef.current && !infoRef.current.contains(event.target)) {
			if (selectedInfoPoint === id) {
				isAnimating.current = false; // Stop the animation only if the InfoPoint is open
			}
			setSelectedInfoPoint(null);
		}
	};

	// Rotate the info point to face the camera and animate the camera
	useFrame(() => {
		if (!ref.current) return;
		if (isRotating) return;
		if (selectedInfoPoint !== id) return;

		// Überprüfen Sie, ob die Kamera bewegt wurde, und lösen Sie den 'change'-Event aus
		if (!camera.position.equals(position)) {
			camera.dispatchEvent({ type: 'change' });
		}
	});

	useEffect(() => {
		// Add the listener when the component mounts
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('touchstart', handleClickOutside);

		// Remove the listener when the component unmounts
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('touchstart', handleClickOutside);
		};
	});

	// Calculate the rotation to face the car
	if (ref.current) {
		const direction = new Vector3(...lookingAt).sub(new Vector3(...position)).normalize();
		ref.current.rotation.y = Math.atan2(direction.x, direction.z);
		ref.current.lookAt(new Vector3(...lookingAt));
	}

	return (
		<mesh position={position} ref={ref} onClick={handleClick}
			onPointerOver={() => setIsHovered(true)}
			onPointerOut={() => setIsHovered(false)}>
			<circleBufferGeometry args={[0.04, 32]} /> {/* radius, segments */}
			<meshBasicMaterial side={THREE.DoubleSide} metalness={1} roughness={1}
				color={isHovered ? selectionColor : (selectedInfoPoint === id ? selectionColor : "crimson")} />
			{selectedInfoPoint === id && (
				<Html position={[0, 0, 0]} ref={infoRef}>
					<StyledInfoBubble>
						<h2>{title}</h2>
						<ul>{info.map((item, index) => (
							<StyledListItem key={index}>
								<StyledLink href={item.link} target="_parent" >
									{item.text}
								</StyledLink>
							</StyledListItem>
						))}</ul>
					</StyledInfoBubble>
				</Html>
			)}
		</mesh>
	);
}
